import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { RootLayout, RouteBoundary } from './components/layout';
import { Spinner } from './components/ui/Spinner';
import { Seo } from './components/seo';
import { useSilentRestore } from './hooks/useAuth';
import { Button } from './components/ui/Button';

// ─── Lazy-loaded route components ────────────────────────────────

const Home = lazy(() => import('./routes/Home'));
const ProductListing = lazy(() => import('./routes/ProductListing'));
const ProductDetail = lazy(() => import('./routes/ProductDetail'));
const CategoryPage = lazy(() => import('./routes/CategoryPage'));
const Search = lazy(() => import('./routes/Search'));

const Login = lazy(() => import('./routes/Login'));
const Register = lazy(() => import('./routes/Register'));
const VerifyEmail = lazy(() => import('./routes/VerifyEmail'));
const ForgotPassword = lazy(() => import('./routes/ForgotPassword'));
const ResetPassword = lazy(() => import('./routes/ResetPassword'));

const RequireAuth = lazy(() => import('./routes/RequireAuth'));
const Cart = lazy(() => import('./routes/Cart'));
const Checkout = lazy(() => import('./routes/Checkout'));
const OrderSuccess = lazy(() => import('./routes/OrderSuccess'));
const Profile = lazy(() => import('./routes/Profile'));
const Orders = lazy(() => import('./routes/Orders'));
const OrderDetail = lazy(() => import('./routes/OrderDetail'));

const RequireAdmin = lazy(() => import('./routes/admin/RequireAdmin'));
const AdminLayout = lazy(() => import('./routes/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./routes/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./routes/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./routes/admin/AdminOrders'));
const AdminCategories = lazy(() => import('./routes/admin/AdminCategories'));
const AdminUsers = lazy(() => import('./routes/admin/AdminUsers'));
const AdminMedia = lazy(() => import('./routes/admin/AdminMedia'));

// ─── New pages ───────────────────────────────────────────────────
const Wishlist = lazy(() => import('./routes/Wishlist'));
const About = lazy(() => import('./routes/About'));
const Contact = lazy(() => import('./routes/Contact'));
const Returns = lazy(() => import('./routes/Returns'));
const Shipping = lazy(() => import('./routes/Shipping'));
const SizeGuide = lazy(() => import('./routes/SizeGuide'));
const Privacy = lazy(() => import('./routes/Privacy'));
const Terms = lazy(() => import('./routes/Terms'));
const Careers = lazy(() => import('./routes/Careers'));

// ─── Inline utility pages (no lazy — tiny) ──────────────────────

function Forbidden() {
  return (
    <>
      <Seo
        title="Access Denied · 403"
        bareTitle
        description="You don't have permission to view this page."
        noIndex
      />
      <div className="container-q section-q flex min-h-[60vh] flex-col items-center justify-center gap-6 text-center">
        <p className="font-[family-name:var(--font-label)] text-[color:var(--color-error)] text-sm uppercase tracking-[0.3em]">
          403 · Forbidden
        </p>
        <h1 className="font-[family-name:var(--font-display)] text-6xl uppercase tracking-tight text-white md:text-8xl">
          Access Denied
        </h1>
        <p className="max-w-md text-[color:var(--color-text-secondary)]">
          You don't have permission to view this page. If you think this is a mistake,
          please sign in with the right account or contact support.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button to="/" size="md" noMagnetic>Back to Home</Button>
          <Button to="/login" variant="secondary" size="md" noMagnetic>Sign In</Button>
        </div>
      </div>
    </>
  );
}

function NotFound() {
  return (
    <>
      <Seo
        title="Page Not Found · 404"
        bareTitle
        description="The page you're looking for doesn't exist."
        noIndex
      />
      <div className="container-q section-q flex min-h-[60vh] flex-col items-center justify-center gap-6 text-center">
        <p className="font-[family-name:var(--font-label)] text-[color:var(--color-gold)] text-sm uppercase tracking-[0.3em]">
          404 · Not Found
        </p>
        <h1 className="font-[family-name:var(--font-display)] text-6xl uppercase tracking-tight text-white md:text-8xl">
          We lost the thread
        </h1>
        <p className="max-w-md text-[color:var(--color-text-secondary)]">
          The page you were looking for has moved, sold out, or never existed.
          Try the homepage, or browse our latest drops.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <Button to="/" size="md" noMagnetic>Back to Home</Button>
          <Button to="/category/jewelry" variant="secondary" size="md" noMagnetic>Shop Jewelry</Button>
          <Button to="/search" variant="ghost" size="md" noMagnetic>Search</Button>
        </div>
      </div>
    </>
  );
}

// ─── Suspense fallback ───────────────────────────────────────────

function PageFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner size="lg" label="Loading page" />
    </div>
  );
}

// ─── App ─────────────────────────────────────────────────────────

/**
 * Root application — wires useSilentRestore for auth hydration,
 * wraps everything in RootLayout, and defines the full route tree
 * with lazy-loaded pages and auth guards.
 */
export default function App() {
  // Hydrate auth from refresh cookie on hard reload.
  // This must run exactly once at App root — NOT per page.
  useSilentRestore();

  return (
    <RootLayout>
      <Suspense fallback={<PageFallback />}>
        <RouteBoundary>
          <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<ProductListing />} />
          <Route path="/products/:slug" element={<ProductDetail />} />
          <Route path="/category/:slug" element={<CategoryPage />} />
          <Route path="/search" element={<Search />} />

          {/* Auth routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Info pages */}
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/size-guide" element={<SizeGuide />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/careers" element={<Careers />} />

          {/* Protected routes */}
          <Route element={<RequireAuth />}>
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order-success/:id" element={<OrderSuccess />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/orders/:id" element={<OrderDetail />} />

            {/* Admin routes (nested under RequireAuth → RequireAdmin) */}
            <Route element={<RequireAdmin />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/categories" element={<AdminCategories />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/media" element={<AdminMedia />} />
              </Route>
            </Route>
          </Route>

          {/* Utility routes */}
          <Route path="/403" element={<Forbidden />} />
          <Route path="*" element={<NotFound />} />
          </Routes>
        </RouteBoundary>
      </Suspense>
    </RootLayout>
  );
}
